(() => {
  class BlobWaves {
    constructor(canvas, opts = {}) {
      this.canvas = canvas;
      this.ctx = canvas.getContext('2d');
      this.running = false;
      this.ticker = this.ticker.bind(this);
      this.lastTime = 0;                          // ★ NEW: time-based animation

      const d = (name, fallback) => {
        const v = canvas.dataset[name];
        return v == null ? fallback : (v === '' ? fallback : v);
      };

      const isMobile = window.matchMedia('(max-width: 767px)').matches;

      this.options = {
        count: +d('count', 5),
        rangeX: +d('rangeX', 20),
        rangeY: +d(isMobile ? 'rangeYMobile' : 'rangeYDesktop', isMobile ? 20 : 80),
        heightDesktop: +d('heightDesktop', 200),
        heightMobile: +d('heightMobile', 100),
        thickness: +d('thickness', 10),
        strokeColor: d('stroke', '#F4F6F8'),
        level: parseFloat(d('level', 0.35)),
        curved: d('curved', 'true') !== 'false',
        speedPx: (opts.speedPx || 15),
        fillColor: d('fill', '#FFFFFF'),
        fillStyleFactory: null,
        ...opts,
      };

      this.points = [];
      this.dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (e.isIntersecting) this.start();
          else this.stop();
        }
      });
      this.prm = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onPRM = () => this.prm.matches ? this.stop() : this.start();

      this.resize();
      this.makePoints();
      this.bind();

      this.io.observe(this.canvas);
      if (!this.prm.matches) this.start();
    }

    bind() {
      this.resizeObserver.observe(this.canvas);
      this.prm.addEventListener?.('change', this.onPRM);
    }

    unbind() {
      this.resizeObserver.disconnect();
      this.io.disconnect();
      this.prm.removeEventListener?.('change', this.onPRM);
    }

    destroy() {
      this.stop();
      this.unbind();
      this.points = [];
    }

    start() {
      if (this.running) return;
      this.running = true;
      this.lastTime = performance.now();          // ★ NEW
      this.tick();
    }

    stop() {
      this.running = false;
      cancelAnimationFrame(this.rafId);
    }

    tick() {
      this.rafId = requestAnimationFrame(this.ticker);
    }

    ticker = () => {
      if (!this.running) return;
      // ★ NEW: compute delta time for smooth, FPS-independent motion
      const now = performance.now();
      let dt = now - this.lastTime;
      this.lastTime = now;
      // clamp crazy tab-jump deltas
      if (dt > 66) dt = 66;

      this.updatePoints(dt);                      // ★ CHANGED: pass dt
      this.render();
      this.tick();
    };

    resize() {
      const cssW = this.canvas.clientWidth || this.canvas.getBoundingClientRect().width || window.innerWidth;
      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      const cssH = isMobile ? this.options.heightMobile : this.options.heightDesktop;

      this.canvas.width  = Math.max(1, Math.floor(cssW * this.dpr));
      this.canvas.height = Math.max(1, Math.floor(cssH * this.dpr));
      this.canvas.style.height = cssH + 'px';
      const ctx = this.ctx;
      ctx.setTransform(1,0,0,1,0,0);
      ctx.scale(this.dpr, this.dpr);

      ctx.lineJoin = 'round';
      ctx.lineWidth = this.options.thickness;
      ctx.strokeStyle = this.options.strokeColor;

      this.makePoints();
    }

    makePoints() {
      const w = this.canvas.clientWidth || this.canvas.width / this.dpr;
      const h = this.canvas.clientHeight || this.canvas.height / this.dpr;
      const { count, rangeX, level } = this.options;

      this.points.length = 0;
      const total = count + 2;
      const step = (w + 2 * rangeX) / (count - 1);
      const baseY = h - h * level;

      for (let i = total; i--; ) {
        const x = step * (i - 1) - rangeX;
        const y = baseY;
        this.points.push(new Point({ x, y }, this.options)); // Point handles its own timing
      }
    }

    // ★ CHANGED: accept dt (ms)
    updatePoints(dt) {
      for (let i = 0; i < this.points.length; i++) {
        this.points[i].update(dt);
      }
    }

    render() {
      const ctx = this.ctx;
      const w = this.canvas.clientWidth || this.canvas.width / this.dpr;
      const h = this.canvas.clientHeight || this.canvas.height / this.dpr;
      const { thickness } = this.options;
      const pts = this.points;
      if (!pts.length) return;

      ctx.clearRect(0, 0, w, h);

      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);

      for (let i = 0; i < pts.length - 1; i++) {
        const midX = (pts[i].x + pts[i + 1].x) / 2;
        const midY = (pts[i].y + pts[i + 1].y) / 2;
        ctx.quadraticCurveTo(pts[i].x, pts[i].y, midX, midY);
      }

      ctx.lineTo(-this.options.rangeX - thickness, h + thickness);
      ctx.lineTo(w + this.options.rangeX + thickness, h + thickness);
      ctx.closePath();

      const fillStyle = this.options.fillStyleFactory
        ? this.options.fillStyleFactory(ctx, w, h)
        : defaultFill(this.options.fillColor, ctx, w, h);

      ctx.fillStyle = fillStyle;
      ctx.fill();
      ctx.stroke();
    }
  }

  class Point {
    constructor(anchor, options) {
      this.anchorX = anchor.x;
      this.anchorY = anchor.y;
      this.x = anchor.x;
      this.y = anchor.y;
      this.opts = options;
      this.tickMs = 0;                              // ★ NEW
      this.durationMs = 1000;                       // ★ NEW (will be set in setTarget)
      this.setTarget();
    }
    randInt(min, max) { return Math.floor(Math.random() * (max - min + 1) + min); }
    easeQuad(t, b, c, d) { t/=d/2; if (t<1) return c/2*t*t+b; t--; return -c/2*(t*(t-2)-1)+b; }

    setTarget() {
      const { rangeX, rangeY, speedPx } = this.opts;
      this.initialX = this.x;
      this.initialY = this.y;
      this.targetX = this.anchorX + this.randInt(0, 2 * rangeX) - rangeX;
      this.targetY = this.anchorY + this.randInt(0, 2 * rangeY) - rangeY;

      // ★ NEW: constant visual speed — duration depends on distance
      const dx = this.targetX - this.initialX;
      const dy = this.targetY - this.initialY;
      const dist = Math.hypot(dx, dy);                 // pixels
      const pxPerSec = Math.max(1, speedPx);           // guard
      this.durationMs = Math.max(400, (dist / pxPerSec) * 1000); // min 0.4s for micro moves

      this.tickMs = 0;
    }

    // ★ CHANGED: update with dt (ms)
    update(dtMs = 16) {
      this.tickMs += dtMs;

      // progress easing over duration
      const yNow = this.easeQuad(this.tickMs, this.initialY, this.targetY - this.initialY, this.durationMs);
      const xNow = this.easeQuad(this.tickMs, this.initialX, this.targetX - this.initialX, this.durationMs);
      this.y = yNow;
      this.x = xNow;

      if (this.tickMs >= this.durationMs) {
        // land exactly on target then retarget
        this.x = this.targetX;
        this.y = this.targetY;
        this.setTarget();
      }
    }
  }

  function defaultFill(color, ctx, w, h) {
    if (color && typeof color === 'string') {
      return color;
    }
    const g = ctx.createLinearGradient(20, 0, w * 0.6, 0);
    g.addColorStop(0, '#ffffff');
    g.addColorStop(1, '#ffffff');
    return g;
  }

  const instances = new WeakMap();

  function initAll() {
    document.querySelectorAll('canvas.blob-waves').forEach((el) => {
      if (instances.has(el)) return;
      instances.set(el, new BlobWaves(el));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.BlobWaves = BlobWaves;
})();
